import React, { useState } from 'react'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { Box, TextField, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { Button as DesignSystemButton } from '../../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../../shared/components/DefaultModal'
import { sendBookDemoEmail } from '../../../../apiLayer/handlers/cloudFunctionsHandler'
import useAppState from '../../../../hooks/useAppState'

interface BookDemoModalProps {
  isOpen: boolean
  onClose: () => void
  className?: string
}

interface BookDemoFormData {
  fullName: string
  role: string
  email: string
  message: string
}

/**
 * BookDemoModal - Modal with form to request a demo
 */
export const BookDemoModal: React.FC<BookDemoModalProps> = ({ isOpen, onClose, className = '' }) => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const [formData, setFormData] = useState<BookDemoFormData>({
    fullName: '',
    role: '',
    email: '',
    message: ''
  })
  const [errors, setErrors] = useState<Partial<Record<keyof BookDemoFormData, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof BookDemoFormData, string>> = {}

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('landing.sections.bookDemoModal.validationRequired')
    }

    if (!formData.role.trim()) {
      newErrors.role = t('landing.sections.bookDemoModal.validationRequired')
    }

    if (!formData.email.trim()) {
      newErrors.email = t('landing.sections.bookDemoModal.validationRequired')
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('landing.sections.bookDemoModal.validationEmailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange =
    (field: keyof BookDemoFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitSuccess(false)

    try {
      const result = await sendBookDemoEmail(
        {
          fullName: formData.fullName.trim(),
          role: formData.role.trim(),
          email: formData.email.trim(),
          message: formData.message.trim()
        },
        state
      )

      if (result.success) {
        setSubmitSuccess(true)
        // Reset form after a short delay
        setTimeout(() => {
          setFormData({ fullName: '', role: '', email: '', message: '' })
          setSubmitSuccess(false)
          onClose()
        }, 2000)
      } else {
        setErrors({ email: result.error || t('landing.sections.bookDemoModal.error') })
      }
    } catch (error) {
      setErrors({ email: t('landing.sections.bookDemoModal.error') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      showCloseButton={true}
      maxWidth="600px"
      backgroundColor={INTERAX_COLORS.offBlack}
    >
      <Box component="form" onSubmit={handleSubmit}>
        {/* Title */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline4,
            color: INTERAX_COLORS.white,
            mb: 2
          }}
        >
          {t('landing.sections.bookDemoModal.title')}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body2,
            color: INTERAX_COLORS.white,
            mb: 3,
            opacity: 0.8
          }}
        >
          {t('landing.sections.bookDemoModal.description')}
        </Typography>

        {/* Success Message */}
        {submitSuccess && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: INTERAX_COLORS.green + '20',
              borderRadius: '8px',
              border: `1px solid ${INTERAX_COLORS.green}`
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.green
              }}
            >
              {t('landing.sections.bookDemoModal.success')}
            </Typography>
          </Box>
        )}

        {/* Full Name */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.white,
              mb: 1
            }}
          >
            {t('landing.sections.bookDemoModal.fullName')} <span style={{ color: INTERAX_COLORS.redError }}>*</span>
          </Typography>
          <TextField
            value={formData.fullName}
            onChange={handleChange('fullName')}
            placeholder={t('landing.sections.bookDemoModal.fullNamePlaceholder')}
            fullWidth
            required
            error={!!errors.fullName}
            helperText={errors.fullName}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: INTERAX_COLORS.offBlack,
                color: INTERAX_COLORS.white,
                '& fieldset': {
                  borderColor: errors.fullName ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey
                },
                '&:hover fieldset': {
                  borderColor: errors.fullName ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                },
                '&.Mui-focused fieldset': {
                  borderColor: errors.fullName ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                }
              },
              '& .MuiInputBase-input': {
                color: INTERAX_COLORS.white
              },
              '& .MuiFormHelperText-root': {
                color: INTERAX_COLORS.redError
              }
            }}
          />
        </Box>

        {/* Role */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.white,
              mb: 1
            }}
          >
            {t('landing.sections.bookDemoModal.role')} <span style={{ color: INTERAX_COLORS.redError }}>*</span>
          </Typography>
          <TextField
            value={formData.role}
            onChange={handleChange('role')}
            placeholder={t('landing.sections.bookDemoModal.rolePlaceholder')}
            fullWidth
            required
            error={!!errors.role}
            helperText={errors.role}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: INTERAX_COLORS.offBlack,
                color: INTERAX_COLORS.white,
                '& fieldset': {
                  borderColor: errors.role ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey
                },
                '&:hover fieldset': {
                  borderColor: errors.role ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                },
                '&.Mui-focused fieldset': {
                  borderColor: errors.role ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                }
              },
              '& .MuiInputBase-input': {
                color: INTERAX_COLORS.white
              },
              '& .MuiFormHelperText-root': {
                color: INTERAX_COLORS.redError
              }
            }}
          />
        </Box>

        {/* Email */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.white,
              mb: 1
            }}
          >
            {t('landing.sections.bookDemoModal.email')} <span style={{ color: INTERAX_COLORS.redError }}>*</span>
          </Typography>
          <TextField
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            placeholder={t('landing.sections.bookDemoModal.emailPlaceholder')}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: INTERAX_COLORS.offBlack,
                color: INTERAX_COLORS.white,
                '& fieldset': {
                  borderColor: errors.email ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey
                },
                '&:hover fieldset': {
                  borderColor: errors.email ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                },
                '&.Mui-focused fieldset': {
                  borderColor: errors.email ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
                }
              },
              '& .MuiInputBase-input': {
                color: INTERAX_COLORS.white
              },
              '& .MuiFormHelperText-root': {
                color: INTERAX_COLORS.redError
              }
            }}
          />
        </Box>

        {/* Message */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.white,
              mb: 1
            }}
          >
            {t('landing.sections.bookDemoModal.message')}
          </Typography>
          <TextField
            value={formData.message}
            onChange={handleChange('message')}
            placeholder={t('landing.sections.bookDemoModal.messagePlaceholder')}
            fullWidth
            multiline
            rows={4}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: INTERAX_COLORS.offBlack,
                color: INTERAX_COLORS.white,
                '& fieldset': {
                  borderColor: INTERAX_COLORS.thunderGrey
                },
                '&:hover fieldset': {
                  borderColor: INTERAX_COLORS.sun
                },
                '&.Mui-focused fieldset': {
                  borderColor: INTERAX_COLORS.sun
                }
              },
              '& .MuiInputBase-input': {
                color: INTERAX_COLORS.white
              }
            }}
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DesignSystemButton
            variant="primary"
            onClick={() => {
              const syntheticEvent = { preventDefault: () => {} } as React.FormEvent
              handleSubmit(syntheticEvent)
            }}
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? t('landing.sections.bookDemoModal.sending') : t('landing.sections.bookDemoModal.send')}
          </DesignSystemButton>
        </Box>
      </Box>
    </DefaultModal>
  )
}
